import React from 'react';
import { Copy, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { playPopSound } from '../utils/soundUtils';

interface ColorPaletteProps {
  colors: string[];
}

export function ColorPalette({ colors }: ColorPaletteProps) {
  const { isDarkMode } = useTheme();
  const [copiedColors, setCopiedColors] = React.useState<{[key: string]: boolean}>({});

  const handleCopyColor = async (color: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    playPopSound('soft'); // Play sound effect
    try {
      await navigator.clipboard.writeText(color);
      setCopiedColors(prev => {
        const newState = { ...prev };
        // Reset all other colors
        Object.keys(newState).forEach(key => {
          newState[key] = false;
        });
        // Set the current color
        newState[color] = true;
        return newState;
      });
      setTimeout(() => {
        setCopiedColors(prev => ({ ...prev, [color]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy color:', error);
    }
  };

  return (
    <div className={`rounded-xl p-4 ${
      isDarkMode 
        ? 'bg-black border-zinc-800' 
        : 'bg-white/50 backdrop-blur-sm border-gray-200'
    } border transition-transform duration-300 hover:scale-[1.01]`}>
      <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Color Palette
      </h3>
      <div className="grid grid-cols-1 gap-3">
        {colors.map((color, index) => (
          <div 
            key={`${color}-${index}`}
            className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
              isDarkMode ? 'bg-zinc-900 hover:bg-zinc-800' : 'bg-gray-50/50 hover:bg-gray-100/50'
            } transform hover:scale-[1.02] active:scale-[0.98]`}
          >
            <div className="flex items-center space-x-3">
              <div 
                className="w-10 h-10 rounded-lg border transition-transform duration-300 hover:scale-110"
                style={{ 
                  backgroundColor: color,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                }}
              />
              <code className={`text-sm font-mono select-all ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {color.toUpperCase()}
              </code>
            </div>
            <button 
              onClick={(e) => handleCopyColor(color, e)}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isDarkMode 
                  ? 'hover:bg-zinc-700 text-gray-400' 
                  : 'hover:bg-gray-200 text-gray-500'
              } hover:scale-110 active:scale-95 touch-manipulation`}
              aria-label={`Copy color ${color}`}
            >
              {copiedColors[color] ? (
                <Check size={16} className="text-green-500 animate-fadeIn" />
              ) : (
                <Copy size={16} className="transition-transform duration-300 hover:rotate-12" />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}